import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, IconButton, Menu, Modal, Portal, Switch, Text, TextInput } from 'react-native-paper';
import { HAMBURGER_ICONS } from '../../../constants/hamburgerIcons';
import { colors, spacing, typography } from '../../../constants/theme';
import { CATEGORY_ICONS } from '../adminCategoryIcons';
import { CategoryForm, ReviewCategory, ReviewItem } from '../AdminDashboard.types';

interface AdminEditCategoryModalProps {
  visible: boolean;
  category: ReviewCategory | null;
  form: CategoryForm;
  categories: ReviewCategory[];
  localItemOrder: ReviewItem[];
  hasOrderChanges: boolean;
  parentMenuVisible: boolean;
  selectedTemplateId: number | null;
  onChangeForm: (form: CategoryForm) => void;
  onChangeParentMenuVisible: (visible: boolean) => void;
  onDismiss: () => void;
  onReorderItem: (itemId: number, direction: 'up' | 'down') => void;
  onSaveItemOrder: () => void;
  onSubmit: () => void;
}

export const AdminEditCategoryModal: React.FC<AdminEditCategoryModalProps> = ({
  visible,
  category,
  form,
  categories,
  localItemOrder,
  hasOrderChanges,
  parentMenuVisible,
  selectedTemplateId,
  onChangeForm,
  onChangeParentMenuVisible,
  onDismiss,
  onReorderItem,
  onSaveItemOrder,
  onSubmit,
}) => {
  if (!category) {
    return null;
  }

  const hasSubcategories = categories.some(candidate => candidate.parentCategoryId === category.id);
  const eligibleItems = localItemOrder.filter(item => !item.isDeprecated && !item.isOnDemand);
  const hasEligibleItems = eligibleItems.length > 0;
  const canEnableHamburger = !hasSubcategories && hasEligibleItems;
  const visibleItems = localItemOrder.filter(item => !item.isDeprecated);

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modal}
      >
        <ScrollView>
          <Text style={styles.modalTitle}>Edit Category: {category.name}</Text>

          <View style={styles.formContainer}>
            <TextInput
              label="Name *"
              value={form.name}
              onChangeText={(text) => onChangeForm({ ...form, name: text })}
              style={styles.formField}
            />

            <TextInput
              label="Description"
              value={form.description}
              onChangeText={(text) => onChangeForm({ ...form, description: text })}
              multiline
              style={styles.formField}
            />

            <Text>Parent Category (Optional)</Text>
            <Menu
              visible={parentMenuVisible}
              onDismiss={() => onChangeParentMenuVisible(false)}
              anchor={
                <Button
                  mode="outlined"
                  onPress={() => onChangeParentMenuVisible(true)}
                  style={styles.formField}
                >
                  {form.parentCategoryId
                    ? categories.find(c => c.id.toString() === form.parentCategoryId)?.name || 'Select Parent Category'
                    : 'No Parent (Top Level)'
                  }
                </Button>
              }
            >
              <Menu.Item
                title="No Parent (Top Level)"
                onPress={() => {
                  onChangeForm({ ...form, parentCategoryId: '' });
                  onChangeParentMenuVisible(false);
                }}
              />
              {categories
                .filter(candidate => candidate.templateId === selectedTemplateId && candidate.id !== category.id && !candidate.showInHamburger)
                .map(candidate => (
                  <Menu.Item
                    key={candidate.id}
                    title={candidate.name}
                    onPress={() => {
                      onChangeForm({ ...form, parentCategoryId: candidate.id.toString() });
                      onChangeParentMenuVisible(false);
                    }}
                  />
                ))}
            </Menu>

            <Text>Icon</Text>
            <View style={styles.iconGrid}>
              {CATEGORY_ICONS.map(iconOption => (
                <Button
                  key={iconOption.value}
                  mode={form.icon === iconOption.value ? 'contained' : 'outlined'}
                  onPress={() => onChangeForm({ ...form, icon: iconOption.value })}
                  style={styles.iconButton}
                >
                  {iconOption.label}
                </Button>
              ))}
            </View>

            <View style={styles.hamburgerSettings}>
              <View style={styles.switchRow}>
                <Switch
                  value={form.showInHamburger}
                  onValueChange={(value) => {
                    if (!value || canEnableHamburger) {
                      onChangeForm({ ...form, showInHamburger: value });
                    }
                  }}
                />
                <Text style={styles.switchLabel}>
                  Show in Hamburger Menu
                </Text>
              </View>
              {!canEnableHamburger && form.showInHamburger && (
                <Text style={styles.invalidHelperText}>
                  {hasSubcategories
                    ? '⚠️ Currently invalid: category has sub-categories. Remove them or turn off hamburger before saving.'
                    : '⚠️ Currently invalid: category has no active items. Add items or turn off hamburger before saving.'}
                </Text>
              )}
              {!canEnableHamburger && !form.showInHamburger && (
                <Text style={styles.helperText}>
                  {hasSubcategories
                    ? 'ℹ️ Only leaf categories (no sub-categories) can be in hamburger menu'
                    : 'ℹ️ Category must have at least one active item to enable hamburger menu'}
                </Text>
              )}
              {form.showInHamburger && (
                <>
                  <Text style={styles.iconLabel}>Hamburger Icon</Text>
                  <View style={styles.iconGrid}>
                    {HAMBURGER_ICONS.map(iconOption => (
                      <Button
                        key={iconOption.value}
                        mode={form.hamburgerIcon === iconOption.value ? 'contained' : 'outlined'}
                        onPress={() => onChangeForm({ ...form, hamburgerIcon: iconOption.value })}
                        style={styles.iconButton}
                      >
                        {iconOption.label}
                      </Button>
                    ))}
                  </View>
                  <TextInput
                    label="Display Order (lower appears first)"
                    value={form.hamburgerOrder?.toString() || '0'}
                    onChangeText={(text) => {
                      const num = parseInt(text, 10) || 0;
                      onChangeForm({ ...form, hamburgerOrder: Math.max(0, num) });
                    }}
                    keyboardType="numeric"
                    style={styles.displayOrderField}
                  />
                  <Text style={styles.helperText}>
                    ℹ️ Items in this category will appear in the review hamburger menu
                  </Text>
                </>
              )}
            </View>

            <Text style={styles.sectionTitle}>Items in this Category</Text>
            {visibleItems.map((item, index) => (
              <View key={item.id} style={styles.itemOrderRow}>
                <View style={styles.itemOrderInfo}>
                  <Text style={styles.itemOrderIndex}>{index + 1}.</Text>
                  <Text style={styles.itemOrderName}>{item.name}</Text>
                  {item.isDeprecated && (
                    <Text style={styles.deactivatedBadge}>Deactivated</Text>
                  )}
                </View>
                <View style={styles.reorderButtons}>
                  <IconButton
                    icon="chevron-up"
                    size={20}
                    onPress={() => onReorderItem(item.id, 'up')}
                    style={styles.reorderIconButton}
                    mode="outlined"
                    disabled={index === 0}
                  />
                  <IconButton
                    icon="chevron-down"
                    size={20}
                    onPress={() => onReorderItem(item.id, 'down')}
                    style={styles.reorderIconButton}
                    mode="outlined"
                    disabled={index === visibleItems.length - 1}
                  />
                </View>
              </View>
            ))}

            {hasOrderChanges && (
              <View style={styles.orderChangesNotice}>
                <Text style={styles.orderChangesText}>Items have been reordered. Click Save to apply changes.</Text>
                <Button
                  mode="contained"
                  onPress={onSaveItemOrder}
                  style={styles.saveOrderButton}
                >
                  Save Order
                </Button>
              </View>
            )}
          </View>

          <View style={styles.formRow}>
            <Button
              mode="outlined"
              onPress={onDismiss}
              style={styles.formField}
            >
              Cancel
            </Button>
            <Button
              mode="contained"
              onPress={onSubmit}
              style={styles.formField}
            >
              Update
            </Button>
          </View>
        </ScrollView>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.background,
    margin: spacing.lg,
    padding: spacing.lg,
    borderRadius: 8,
    maxHeight: '90%',
    minHeight: '70%',
  },
  modalTitle: {
    ...typography.h2,
    marginBottom: spacing.lg,
  },
  formContainer: {
    gap: spacing.md,
  },
  formField: {
    flex: 1,
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingHorizontal: spacing.sm,
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  iconGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  iconButton: {
    minWidth: 100,
    marginBottom: spacing.xs,
  },
  hamburgerSettings: {
    marginTop: spacing.md,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  switchLabel: {
    marginLeft: spacing.sm,
  },
  helperText: {
    color: colors.textSecondary,
    fontSize: 12,
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
  },
  invalidHelperText: {
    color: colors.error,
    fontSize: 12,
    marginBottom: spacing.sm,
  },
  iconLabel: {
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
  },
  displayOrderField: {
    marginTop: spacing.sm,
  },
  sectionTitle: {
    ...typography.h2,
    marginBottom: spacing.md,
  },
  itemOrderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.surface,
  },
  itemOrderInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  itemOrderIndex: {
    ...typography.body,
    fontWeight: 'bold',
    marginRight: spacing.sm,
    minWidth: 25,
  },
  itemOrderName: {
    ...typography.body,
    flex: 1,
  },
  deactivatedBadge: {
    ...typography.caption,
    color: colors.error,
    backgroundColor: colors.surface,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: 4,
  },
  reorderButtons: {
    flexDirection: 'column',
    gap: spacing.xs,
    marginRight: spacing.sm,
  },
  reorderIconButton: {
    margin: 0,
    borderWidth: 1,
    borderColor: colors.gray300,
  },
  orderChangesNotice: {
    backgroundColor: colors.primary + '10',
    padding: spacing.md,
    borderRadius: 8,
    marginTop: spacing.md,
    alignItems: 'center',
  },
  orderChangesText: {
    ...typography.body,
    color: colors.primary,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  saveOrderButton: {
    backgroundColor: colors.primary,
  },
});

export default AdminEditCategoryModal;
